import React, { useId } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Element } from 'react-scroll'
import MasterData from '../../data/MasterData'
import TrainLaneData from '../../data/TrainLaneData'
import NearbyTrain from './NearbyTrain'
import NearbyMultipleTrain from './NearbyMultipleTrain'
import { stationNumberClass } from '../../utils/ClassHelper'

const NearbyTrainPositionTwinStationItem = (props) => {
  const { station, trains, themeName, intl } = props
  const locale = intl.locale
  const uniqueId = useId()

  // 次の駅ID
  const nextStationIds = (direction) => {
    if (!station) return null

    if ( direction === 'up') {
      return [MasterData.upNextStationId(station.itemLineId, station.id)]
    } else {
      return [station.id]
    }
  }

  // nextStationId に向かっている営業運転列車を返す
  const trainsToward = (direction) => {
    const nextStationIdsData = nextStationIds(direction)
    if (!nextStationIdsData) return []

    return trains.filter(train => (
      station.itemLineId === train.line_id &&
      nextStationIdsData.includes(train.next_station_id) && 
      train.direction === direction
    )).sort((a, b) => {
      // positionは次駅に近い方から昇順に設定されているので、上りでは昇順、下りでは降順にソートする
      if (direction === 'up') {
        return a.position - b.position;
      } else {
        return b.position - a.position;
      }
    });
  }

  // 駅の指定された方向に止まっている電車を返す
  const trainsOnStation = (direction) => {
    if (!station) return []
    return trains.filter(train => (
      !train.next_station_id && // JR線内の駅間在線列車は、station_id(後方駅), next_station_id両方が入っているので除く
      train.station_id === station.id &&
      train.direction === direction
    ))
  }
    
  const renderTrain = (trainsData) => {
    if (trainsData.length == 0) {
      return
    } else if (trainsData.length == 1) {
      return <NearbyTrain
        {...props}
        key={`train${trainsData[0].operation_number}`}
        train={trainsData[0]}
        intl={intl}
      />  
    } else {
      const sortedTrains = trainsData.slice().sort((a, b) => {
        const aOrder = (a.train_kind_id && MasterData.trainKindById(a.train_kind_id).station_detail_order) ? MasterData.trainKindById(a.train_kind_id).station_detail_order : 99
        const bOrder = (b.train_kind_id && MasterData.trainKindById(b.train_kind_id).station_detail_order) ? MasterData.trainKindById(b.train_kind_id).station_detail_order : 99
        return aOrder - bOrder
      })
      
      return <NearbyMultipleTrain
        {...props}
        key={`train${trainsData[0].operation_number}`}
        trains={sortedTrains}
        intl={intl}
        />
    }
  }

  const renderLineElements = () => {
    return (
      station.lineClasses.map((lineClass, index) => {
        return <div key={index} className={`d-TrainPosition-line ${lineClass}`} />
      })
    )
  }

  const renderStationName = () => {
    if (station?.hiddenStation) {
      return
    }

    return(
      <div className={`d-TrainPosition-stationInfo ${['shinyokohamaBranchEnd', 'jrBranchEnd'].includes(station.kind) ? 'is-hidden' : ''}`}>
        <div className={`d-TrainPosition-stationInfoLink ${station.linkDisabled ? 'is-disabled' : ''}`}>
          <div className='d-TrainPosition-stationNumbers'>
            {station.eki_number.split(',').map(ekiNumber => (
              <div className={`d-TrainPosition-stationNumber ${stationNumberClass(ekiNumber, themeName)}`} key={`stationNumber${ekiNumber}`}></div>)
            )}
          </div>
          {renderStationNameMain()}
          {renderStationNameEn()}
        </div>
      </div>
    )
  }

  const renderStationNameMain = () => {
    // 羽沢駅だけ改行を入れる
    if (station.id === 28) {
      return(
        <p className={`d-TrainPosition-stationName01 d-TrainPosition-stationName01--hazawaYokohamaKokudai is_${locale}`}>{station[`name_${locale}`]}</p>
      )
    } else {
      return(
        <p className={`d-TrainPosition-stationName01 is_${locale}`}>{station[`name_${locale}`]}</p>
      )
    }
  }

  const renderStationNameEn = () => {
    // 羽沢駅だけ改行を入れる
    if (station.id === 28) {
      return(
        <p className={`d-TrainPosition-stationName02 is_${locale}`}>Hazawa<br/> yokohama-kokudai</p>
      )
    } else {
      return(
        <p className={`d-TrainPosition-stationName02 is_${locale}`}>{station.name_en}</p>
      )
    }
  }

  const renderRunningTrainPositionBlockRow = (direction) => {
    return(
      <div className="d-TrainPosition-blockRow">
        <div className="d-TrainPosition-blockItem">
          {renderTrain(trainsToward(direction))}
        </div>
      </div>
    )
  }

  const renderStationTrainPositionBlockRow = (direction) => {
    if (station?.hiddenStation) {
      return
    }

    return(
      <div className="d-TrainPosition-blockRow">
        <div className="d-TrainPosition-blockItem">
          {renderTrain(trainsOnStation(direction))}
        </div>
      </div>
    )
  }

  const itemClass = station.itemClass === undefined ? '' : station.itemClass

  if (station.id === null) {
    return (
      <li className={`d-TrainPosition-stationItem ${itemClass}`} >
        <Element id={`station-${uniqueId}`} />
        {renderLineElements()}

        <div className="d-TrainPosition-block">
        </div>

        <div className="d-TrainPosition-block is_on_station">
        </div>
      </li>
    )
  } else {
    return (
      <li className={`d-TrainPosition-stationItem ${itemClass}`} >
        <Element id={`station${station.id}`} />
        {renderStationName()}
        {renderLineElements()}

        <div className="d-TrainPosition-block">
          {/* 駅間に停車中 & 上り */}
          {renderRunningTrainPositionBlockRow('up')}

          {/* 駅間に停車中 & くだり */}
          {renderRunningTrainPositionBlockRow('down')}

        </div>

        <div className="d-TrainPosition-block is_on_station">
          {/* 駅の上に停車中 & 上り */}
          {renderStationTrainPositionBlockRow('up')}

          {/* 駅の上に停車中 & くだり */}
          {renderStationTrainPositionBlockRow('down')}
        </div>
      </li>
    )
  }
}

NearbyTrainPositionTwinStationItem.propTypes = {
  station: PropTypes.object,
  trains: PropTypes.array.isRequired,
  themeName: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
}

export default injectIntl(NearbyTrainPositionTwinStationItem)
